/**
* WYSIWYG JS
* -----------------------------------------------------------------------------
*
* All the JS for the WYSIWYG component.
*
* Breast Reduction Calculator (Schnur Scale): makes the calculator markup pasted
* into the editor (<div class="schnur-calculator">…) interactive. The Schnur
* Scale values are read from the tables in that markup, so editing a table row
* also updates the calculation.
*/

/*
 * Example of importing modules if needed. Like for scroll magic / gsap
 */

// import {gsap} from 'gsap';
// import {ScrollTrigger} from 'gsap/ScrollTrigger.js';
// gsap.registerPlugin( ScrollTrigger );

function formatNumber( value, decimals = 0 ) {
  return value.toLocaleString( 'en-US', {minimumFractionDigits: decimals, maximumFractionDigits: decimals} );
}

// Reads [{bsa, grams, row}] from the Schnur Scale table rows, sorted by BSA
function readSchnurScale( root ) {
  return Array.from( root.querySelectorAll( '.schnur-calculator__scroll tbody tr' ) )
    .map( ( row ) => {
      const cells = row.querySelectorAll( 'td' );
      return {
        bsa: cells.length > 1 ? parseFloat( cells[0].textContent ) : NaN,
        grams: cells.length > 1 ? parseFloat( cells[1].textContent.replace( /[^\d.]/g, '' ) ) : NaN,
        row,
      };
    } )
    .filter( ( entry ) => !isNaN( entry.bsa ) && !isNaN( entry.grams ) )
    .sort( ( a, b ) => a.bsa - b.bsa );
}

// Linear interpolation between the scale's rows, clamped to its range
function schnurGrams( scale, bsa ) {
  const first = scale[0];
  const last = scale[scale.length - 1];
  if ( bsa <= first.bsa ) {
    return first.grams;
  }
  if ( bsa >= last.bsa ) {
    return last.grams;
  }
  for ( let i = 0; i < scale.length - 1; i++ ) {
    const low = scale[i];
    const high = scale[i + 1];
    if ( bsa <= high.bsa ) {
      return low.grams + ( high.grams - low.grams ) * ( bsa - low.bsa ) / ( high.bsa - low.bsa );
    }
  }
}

// The scale row closest to a BSA
function nearestSchnurRow( scale, bsa ) {
  return scale.reduce( ( best, entry ) => ( Math.abs( entry.bsa - bsa ) < Math.abs( best.bsa - bsa ) ? entry : best ) ).row;
}

function initSchnurCalculator( root ) {
  const el = ( name ) => root.querySelector( `[data-rs="${name}"]` );
  const scale = readSchnurScale( root );
  if ( !el( 'form' ) || scale.length < 2 ) {
    return;
  }

  const scaleMin = scale[0].bsa;
  const scaleMax = scale[scale.length - 1].bsa;
  let units = 'imperial';

  const num = ( name ) => {
    const value = el( name ).value.trim();
    return value === '' ? NaN : parseFloat( value );
  };

  const setError = ( names, errorName, invalid, showErrors ) => {
    if ( !showErrors ) {
      return;
    }
    names.forEach( ( name ) => el( name ).setAttribute( 'aria-invalid', invalid ? 'true' : 'false' ) );
    el( errorName ).textContent = invalid ? 'Please enter a valid value' : '';
  };

  // Returns {cm, kg} or null when the inputs are missing or out of range
  const read = ( showErrors ) => {
    let cm, kg, heightInvalid, weightInvalid;

    if ( units === 'imperial' ) {
      const ft = num( 'ft' );
      const inches = isNaN( num( 'in' ) ) ? 0 : num( 'in' );
      const lb = num( 'lb' );
      const totalInches = ft * 12 + inches;
      heightInvalid = isNaN( ft ) || inches < 0 || inches >= 12 || totalInches < 48 || totalInches > 96;
      weightInvalid = isNaN( lb ) || lb < 70 || lb > 700;
      cm = totalInches * 2.54;
      kg = lb * 0.45359237;
      setError( ['ft', 'in'], 'ft-error', heightInvalid, showErrors );
      setError( ['lb'], 'lb-error', weightInvalid, showErrors );
    } else {
      cm = num( 'cm' );
      kg = num( 'kg' );
      heightInvalid = isNaN( cm ) || cm < 122 || cm > 244;
      weightInvalid = isNaN( kg ) || kg < 32 || kg > 318;
      setError( ['cm'], 'cm-error', heightInvalid, showErrors );
      setError( ['kg'], 'kg-error', weightInvalid, showErrors );
    }

    return heightInvalid || weightInvalid ? null : {cm, kg};
  };

  const clearHighlight = () => scale.forEach( ( entry ) => entry.row.classList.remove( 'is-active' ) );

  const reset = () => {
    ['bsa', 'ft2', 'grams', 'weight', 'total'].forEach( ( name ) => {
      el( name ).textContent = '–';
    } );
    el( 'note' ).hidden = true;
    clearHighlight();
  };

  const render = ( {cm, kg} ) => {
    const bsa = Math.round( Math.sqrt( cm * kg / 3600 ) * 100 ) / 100; // Mosteller
    const grams = Math.round( schnurGrams( scale, bsa ) );
    const ounces = grams / 28.349523125;
    const note = el( 'note' );

    el( 'bsa' ).textContent = bsa.toFixed( 2 );
    el( 'ft2' ).textContent = formatNumber( bsa * 10.7639, 2 );
    el( 'grams' ).textContent = formatNumber( grams );
    el( 'weight' ).textContent = ounces >= 16 ? `${formatNumber( ounces / 16, 1 )} lb` : `${formatNumber( ounces, 1 )} oz`;
    el( 'total' ).textContent = formatNumber( grams * 2 );

    if ( bsa < scaleMin ) {
      note.textContent = `Your BSA is below the lowest value on the Schnur Scale (${scaleMin.toFixed( 2 )} m²). The scale minimum of ${formatNumber( scale[0].grams )} g per breast is shown.`;
    } else if ( bsa > scaleMax ) {
      note.textContent = `Your BSA is above the highest value on the Schnur Scale (${scaleMax.toFixed( 2 )} m²). The scale maximum is shown. Please consult with our surgeons.`;
    }
    note.hidden = bsa >= scaleMin && bsa <= scaleMax;

    // Highlight the nearest row and scroll only its table box, not the page
    clearHighlight();
    const row = nearestSchnurRow( scale, bsa );
    const box = row.closest( '.schnur-calculator__scroll' );
    const head = box.querySelector( 'thead' );
    const headHeight = head ? head.offsetHeight : 0;
    row.classList.add( 'is-active' );
    box.scrollTop = Math.max( 0, row.offsetTop - headHeight - ( box.clientHeight - headHeight ) / 2 + row.offsetHeight / 2 );
  };

  const update = ( showErrors ) => {
    const values = read( showErrors );
    if ( values ) {
      render( values );
    } else {
      reset();
    }
  };

  el( 'form' ).addEventListener( 'submit', ( e ) => {
    e.preventDefault();
    update( true );
  } );

  root.querySelectorAll( 'input' ).forEach( ( input ) => {
    input.addEventListener( 'input', () => update( false ) );
    input.addEventListener( 'blur', () => {
      if ( input.value !== '' ) {
        update( true );
      }
    } );
  } );

  // Unit toggle: convert the current values so nothing has to be retyped
  root.querySelectorAll( '[data-units]' ).forEach( ( button ) => {
    button.addEventListener( 'click', () => {
      const next = button.getAttribute( 'data-units' );
      if ( next === units ) {
        return;
      }
      const values = read( false );
      units = next;

      root.querySelectorAll( '[data-units]' ).forEach( ( b ) => b.setAttribute( 'aria-pressed', b === button ? 'true' : 'false' ) );
      root.querySelectorAll( '[data-group]' ).forEach( ( group ) => {
        group.hidden = group.getAttribute( 'data-group' ) !== units;
      } );
      root.querySelectorAll( '.schnur-calculator__error' ).forEach( ( error ) => {
        error.textContent = '';
      } );
      root.querySelectorAll( 'input' ).forEach( ( input ) => input.removeAttribute( 'aria-invalid' ) );

      if ( values ) {
        if ( units === 'metric' ) {
          el( 'cm' ).value = Math.round( values.cm * 10 ) / 10;
          el( 'kg' ).value = Math.round( values.kg * 10 ) / 10;
        } else {
          const totalInches = values.cm / 2.54;
          let ft = Math.floor( totalInches / 12 );
          let inches = Math.round( ( totalInches - ft * 12 ) * 2 ) / 2;
          if ( inches >= 12 ) {
            ft += 1;
            inches = 0;
          }
          el( 'ft' ).value = ft;
          el( 'in' ).value = inches;
          el( 'lb' ).value = Math.round( values.kg / 0.45359237 * 10 ) / 10;
        }
      }
      update( false );
    } );
  } );
}

( function( app ) {
  const COMPONENT = {

    // Not "wysiwyg": that class is the theme's shared rich-text wrapper
    className: 'wysiwyg-component',
    selector: function() {
      return '.' + this.className;
    },
    // Fires after common.init, before .finalize and common.finalize
    init: function() {
      $( this.selector() ).find( '.schnur-calculator' ).each( function( index, element ) {
        initSchnurCalculator( element );
      } );
    },
    finalize: function() {
    },
  };

  // Hooks the component into the app
  app.registerComponent( 'wysiwyg', COMPONENT );
} )( app );
